import { AppBar, Box, Button, ButtonGroup, Toolbar, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { selectUser, UserState } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../app/hooks";
import { RoutineInfo, routineSlice, retrieveRoutine, selectRoutine } from "../../../redux/routine/routineSlice";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { retrieveSchool, schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import RoutinePageTitle from "../basics/RoutinePageTitle";
import RoutinePageAvatar from "../basics/RoutinePageAvatar";
import { routineInsUpdSlice, routineDelete } from "../../../redux/routine/routineInsUpdSlice";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import { imageInsSlice } from "../../../redux/image/imageInsSlice";
import MenuIcon from "@mui/icons-material/Menu";
import { LoadState } from "../../../redux/common";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { anyPermission, PermissionId } from "../../../app/permissions";
import { routeRoutineEdit, routeRoutineImage } from "../../../routes/routine";
import { routeSchool } from "../../../routes/school";
import { usersForPermissionsSlice } from "../../../redux/user/usersForPermissionsSlice";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import { TopBar } from "../../../features/top/TopBar";
import { useEffect, useState } from "react";

import HomeIcon from "@mui/icons-material/Home";
import RoutineItemList from "./RoutineItemList";
import EditIcon from "@mui/icons-material/Edit";
import TooltipButton from "../../../components/TooltipButton";

type RoutineContentOptions = "main" ;

export function Routine() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const school = useSelector(selectSchool);
    const routine = useSelector(selectRoutine);
    const [contentSelected, setContentSelected] = useState("main" as RoutineContentOptions);
    const { schoolId, routineId } = useParams();

    useEffect(() => {
        if (school.selected && school.selected.id != schoolId) {
            dispatch(schoolSlice.actions.clean());
        }
        if (school.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchool({ schoolId: schoolId }));
        }
    }, [schoolId, school, user]);

    useEffect(() => {
        if (routineId && routine.selected && routine.selected.id != routineId) {
            dispatch(routineSlice.actions.clean());
        }
        if (routineId && routine.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutine({ routineId: routineId }));
        }
    }, [routineId, routine, user]);

    if ((!schoolId) || (!routineId)) {
        return <ElementMissingContext />;
    }

    const canEdit = canEditRoutine(user, schoolId);

    const cleanup = () => {
        dispatch(schoolSlice.actions.clean());
        dispatch(routineSlice.actions.clean());
        dispatch(usersForPermissionsSlice.actions.clean());
    };

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string|null>(null);

    const handleDeleteClick = () => setOpenDeleteDialog(true);
    const handleDeleteCancel = () => { setOpenDeleteDialog(false); setDeleteError(null); };
    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            await dispatch(routineDelete({ routineId: routineId }) as any);
            setOpenDeleteDialog(false);
            navigate(routeSchool(schoolId, "routine"));
        } catch (err: any) {
            setDeleteError("Erro ao remover rotina. Tente novamente.");
            setDeleting(false);
        }
    };

    const options: ContextMenuOptions[] = [];
    options.push({ label: "Atualizar", click: cleanup });
    if (canEdit) {
        options.push({
            label: "Editar", click: () => {
                if (routine.selected) {
                    const newRoutine: RoutineInfo = { ...routine.selected };
                    dispatch(routineInsUpdSlice.actions.set(newRoutine));
                    navigate(routeRoutineEdit(schoolId, routineId));
                }
            }
        });
        options.push({
            label: "Alterar Imagem", click: () => {
                if (routine.selected) {
                    dispatch(imageInsSlice.actions.clean());
                    navigate(routeRoutineImage(schoolId, routineId));
                }
            }
        });
        options.push({ label: "Remover", click: handleDeleteClick });
    }
    const topMenu = <ContextMenu label={<MenuIcon />} options={options} />;

    const breadcrumbs = [
        {
            label: school.selected?.name ? school.selected?.name : "Escola",
            href: routeSchool(schoolId, "routine")
        },
        {
            label: routine.selected?.name || "Rotina",
        },
    ];

    return <>
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} aria-labelledby="delete-routine-dialog-title">
            <DialogTitle id="delete-routine-dialog-title" sx={{ color: 'error.main' }}>Remover Rotina</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Esta ação é <b>irreversível</b> e todos os dados associados serão perdidos!
                </DialogContentText>
                <DialogContentText sx={{ color: 'error.main' }}>
                    Tem certeza que deseja <b>REMOVER</b> a rotina <b>{routine.selected?.name}</b>?
                </DialogContentText>
                {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} color="primary" variant="contained" disabled={deleting}>Cancelar</Button>
                <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting} autoFocus>
                    {deleting ? "Removendo..." : "Remover"}
                </Button>
            </DialogActions>
        </Dialog>
        <PageAuthenticatedLoading
        topbar={<TopBar
            topMenu={topMenu}
            title={
                <Typography variant="h6" component="div">
                    Rotina
                </Typography>
            }
            breadcrumbs={breadcrumbs}
        />}
        loading={{
            states: [school.state, routine.state],
            content: () => {
                const body = () => {
                    switch (contentSelected) {
                        case "main": return <RoutineItemList schoolId={schoolId} routineId={routineId} />;
                    }
                };

                return <>
                    <AppBar key={"app-name"} id={"app-name"} position="static" color="default">
                        <Toolbar>
                            <RoutinePageAvatar />
                            <RoutinePageTitle schoolId={schoolId} routineId={routineId} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate(routeSchool(schoolId, "routine"))}>Voltar</Button>
                        </Toolbar>
                    </AppBar>
                    <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                        <Toolbar>
                            <ButtonGroup variant="contained" color="primary">
                                {/* <Button onClick={() => setContentSelected("main")}>
                                    <HomeIcon />
                                </Button> */}
                                <TooltipButton
                                    option="home"
                                    optionSelected={contentSelected}
                                    onClick={() => setContentSelected("main")}
                                    tooltip="Página Inicial">
                                    <HomeIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="edit-routine"
                                    optionSelected={contentSelected}
                                    disabled={!canEdit}
                                    onClick={() => {
                                            const newRoutine = { ...routine.selected } as RoutineInfo;
                                            dispatch(routineInsUpdSlice.actions.set(newRoutine));
                                            navigate(routeRoutineEdit(schoolId, routineId));
                                        }
                                    }
                                    tooltip="Editar Sala">
                                    <EditIcon />
                                </TooltipButton>
                            </ButtonGroup>
                        </Toolbar>
                    </AppBar>
                    {body()}
                </>;
            },
            error: "Erro ao carregar lista de rotinas",
            retry: cleanup
        }} />
    </>;
}

export function canEditRoutine(user: UserState, schoolId: string) {
    return anyPermission(user.priza.info, schoolId, [PermissionId.SCHOOL_ADMIN]);
}

